import {
  addMonths,
  endOfWeek,
  getDay,
  getDaysInMonth,
  isValid,
  parse,
  parseISO,
  subMonths,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { DateSlotBase } from './type';

const spanishMonths = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

const generateMonthItems = (
  startDay: number,
  endDay: number,
  baseDate: Date,
  disabled: boolean,
  keyPrefix: string,
): DateSlotBase[] => {
  return [...Array(endDay - startDay + 1).keys()].map((index) => ({
    keyValue: `${keyPrefix}-${startDay + index}`,
    disabled,
    label: startDay + index,
    value: new Date(baseDate.getFullYear(), baseDate.getMonth(), startDay + index),
  }));
};

const stringToDate = (dateStringIso: string, { defaultUTC = false } = {}) => {
  const isoDatePattern = /(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})(\.\d+)?([+-]\d{2}:?\d{2}|Z)$/;
  const hasOffset = isoDatePattern.test(dateStringIso);
  const hasTime = dateStringIso.includes('T');
  const dateStringWithTime = hasTime ? dateStringIso : `${dateStringIso}T00:00:00`;
  const dateString = hasOffset ? dateStringWithTime : `${dateStringWithTime}+00:00`;
  const date = defaultUTC ? new Date(dateStringWithTime) : parseISO(dateString);
  return date;
};

export const defaultMinDate = new Date('1900-01-01T00:00:00.000');
export const defaultMaxDate = new Date('2099-12-31T00:00:00.000');

export const applyDefaultDate = (
  date: Date | string | null | undefined,
  defaultDate: Date | null,
  format: string | null = null,
): Date | null => {
  if (date === null || date === undefined) return defaultDate;
  if (typeof date === 'string') {
    const dateObjectParsedByFormat = format
      ? parse(date, format, new Date())
      : stringToDate(date, { defaultUTC: true });
    const dateObject = isValid(dateObjectParsedByFormat)
      ? dateObjectParsedByFormat
      : stringToDate(date, { defaultUTC: true });
    return isValid(dateObject) ? dateObject : defaultDate;
  }
  if (isValid(date)) return date;
  return defaultDate;
};

export const getWeekDayIndex = (date: Date = new Date()): number => {
  const dateFnsIndex = getDay(date);
  if (Number.isNaN(dateFnsIndex)) return 0;
  return dateFnsIndex === 0 ? 6 : dateFnsIndex - 1;
};

export const getCalendarContent = (date: Date = new Date()) => {
  const firstDayOfMonth = startOfMonth(date);
  const firstDayOfMonthIndex = getWeekDayIndex(firstDayOfMonth);
  const totalDaysToShow = 7 * 6;
  const totalDaysOfMonth = getDaysInMonth(date);
  const totalDaysOfPreviousMonth = getDaysInMonth(subMonths(date, 1));
  const daysOfPreviousMonthToShow = firstDayOfMonthIndex;
  const totalDaysOfNextMonth = totalDaysToShow - (totalDaysOfMonth + daysOfPreviousMonthToShow);

  const previousMonthItems = generateMonthItems(
    totalDaysOfPreviousMonth - daysOfPreviousMonthToShow + 1,
    totalDaysOfPreviousMonth,
    subMonths(date, 1),
    true,
    'previous',
  );
  const currentMonthItems = generateMonthItems(1, totalDaysOfMonth, date, false, 'current');
  const nextMonthItems = generateMonthItems(
    1,
    totalDaysOfNextMonth,
    addMonths(date, 1),
    true,
    'next',
  );

  return [...previousMonthItems, ...currentMonthItems, ...nextMonthItems];
};

export const getMonthName = (monthIndex: number): string => {
  return monthIndex < 12 ? spanishMonths[monthIndex] : '';
};

export const getWeekSelectedWeekLimits = (
  date: Date = new Date(),
): { startDate: Date; endDate: Date } => {
  if (Number.isNaN(date.getTime())) return { startDate: null, endDate: null };
  const startDate = startOfWeek(date, { weekStartsOn: 1 });
  const endDate = endOfWeek(date, { weekStartsOn: 1 });
  return { startDate, endDate };
};

export const getDefaultDateBuilder = (value: Date | null | undefined) => {
  return {
    year: value ? value.getFullYear() : new Date().getFullYear(),
    month: value ? value.getMonth() : new Date().getMonth(),
    date: value || null,
    range: value ? getWeekSelectedWeekLimits(value) : null,
  };
};

export const isValidDate = (dateToCheck: unknown): boolean => {
  return dateToCheck instanceof Date && !Number.isNaN(dateToCheck.getTime());
};

export const isValidDateString = (dateString: string, pattern: string): boolean => {
  if (!dateString) return false;
  return isValid(parse(dateString, pattern, new Date()));
};

export const removeSpecialCharacters = (inputString: string): string => {
  const regex = /[^a-zA-Z0-9]/g;
  return inputString.replace(regex, '');
};

export const compareTwoDates = (date1: Date, date2: Date, comparison: string): boolean => {
  const date1UTCYear = date1.getUTCFullYear();
  const date2UTCYear = date2.getUTCFullYear();
  const date1UTCMonth = date1.getUTCMonth();
  const date2UTCMonth = date2.getUTCMonth();
  const date1UTCDate = date1.getUTCDate();
  const date2UTCDate = date2.getUTCDate();
  if (comparison === 'greater') {
    return (
      date1UTCYear > date2UTCYear ||
      (date1UTCYear === date2UTCYear && date1UTCMonth > date2UTCMonth) ||
      (date1UTCYear === date2UTCYear &&
        date1UTCMonth === date2UTCMonth &&
        date1UTCDate > date2UTCDate)
    );
  }
  if (comparison === 'greaterOrEqual') {
    return (
      date1UTCYear > date2UTCYear ||
      (date1UTCYear === date2UTCYear && date1UTCMonth > date2UTCMonth) ||
      (date1UTCYear === date2UTCYear &&
        date1UTCMonth === date2UTCMonth &&
        date1UTCDate >= date2UTCDate)
    );
  }
  if (comparison === 'less') {
    return (
      date1UTCYear < date2UTCYear ||
      (date1UTCYear === date2UTCYear && date1UTCMonth < date2UTCMonth) ||
      (date1UTCYear === date2UTCYear &&
        date1UTCMonth === date2UTCMonth &&
        date1UTCDate < date2UTCDate)
    );
  }
  if (comparison === 'lessOrEqual') {
    return (
      date1UTCYear < date2UTCYear ||
      (date1UTCYear === date2UTCYear && date1UTCMonth < date2UTCMonth) ||
      (date1UTCYear === date2UTCYear &&
        date1UTCMonth === date2UTCMonth &&
        date1UTCDate <= date2UTCDate)
    );
  }
  if (comparison === 'equal') {
    return (
      date1UTCYear === date2UTCYear &&
      date1UTCMonth === date2UTCMonth &&
      date1UTCDate === date2UTCDate
    );
  }
  throw new Error('Invalid comparison operator');
};
